import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const CouponManager = () => {
  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [couponForm, setCouponForm] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingCoupon, setEditingCoupon] = useState(null);
  
  const [formData, setFormData] = useState({
    code: '',
    discount: '',
    minAmount: '',
    expiryDate: new Date(),
    active: true
  });

  // Fetch existing coupons
  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/get-coupons`);
      setCoupons(response.data.coupons);
    } catch (error) {
      setError('Error fetching coupons');
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (editingCoupon) {
        await axios.put(`${process.env.REACT_APP_API_URL}/coupons/${editingCoupon}`, formData);
        setSuccessMessage('Coupon updated successfully!');
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/create-coupons`, formData);
        setSuccessMessage('Coupon created successfully!');
      }
      resetForm();
      fetchCoupons();
      setCouponForm(false); // Close form after successful submission
    } catch (error) {
      setError(error.response?.data?.message || `Error ${editingCoupon ? 'updating' : 'creating'} coupon`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (couponId) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/coupons/${couponId}`);
        setSuccessMessage('Coupon deleted successfully!');
        fetchCoupons();
      } catch (error) {
        setError('Error deleting coupon');
      }
    }
  };

  const handleToggleActive = async (couponId, currentStatus) => {
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/coupons/${couponId}`, {
        active: !currentStatus
      });
      fetchCoupons();
    } catch (error) {
      setError('Error updating coupon status');
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon._id);
    setFormData({
      code: coupon.code,
      discount: coupon.discount,
      minAmount: coupon.minAmount,
      expiryDate: new Date(coupon.expiryDate),
      active: coupon.active
    });
    setCouponForm(true); // Open form when editing
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount: '',
      minAmount: '',
      expiryDate: new Date(),
      active: true
    });
    setEditingCoupon(null);
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-xl sm:text-3xl font-bold">Coupon Management</h2>
        <button
          onClick={() => setCouponForm(true)}
          className="w-auto sm:w-auto px-4 py-2 text-base font-semibold rounded transition-colors bg-black text-white hover:bg-gray-800"
        >
          Create Coupon
        </button>
      </div>

      {/* Create/Edit Coupon Form */}
      {couponForm && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-8">
          <h2 className="text-xl font-semibold mb-4">
            {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
          </h2>
          
          {(error || successMessage) && (
            <div className={`${error ? 'bg-red-100 border-red-400 text-red-700' : 'bg-green-100 border-green-400 text-green-700'} px-4 py-3 rounded mb-4 border`}>
              {error || successMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coupon Code
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount (%)
                </label>
                <input
                  type="number"
                  value={formData.discount}
                  onChange={(e) => setFormData({...formData, discount: e.target.value})}
                  min="0"
                  max="100"
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Amount (₦)
                </label>
                <input
                  type="number"
                  value={formData.minAmount}
                  onChange={(e) => setFormData({...formData, minAmount: e.target.value})}
                  min="0"
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiry Date
                </label>
                <DatePicker
                  selected={formData.expiryDate}
                  onChange={(date) => setFormData({...formData, expiryDate: date})}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  minDate={new Date()}
                  dateFormat="yyyy-MM-dd"
                  required
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({...formData, active: e.target.checked})}
                  className="mr-2"
                />
                <label className="text-sm font-medium text-gray-700">
                  Active
                </label>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full sm:w-auto bg-black text-white py-2 px-4 rounded hover:bg-gray-900 disabled:bg-gray-500"
              >
                {isLoading ? 
                  (editingCoupon ? 'Updating...' : 'Creating...') :
                  (editingCoupon ? 'Update Coupon' : 'Create Coupon')}
              </button>

              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setCouponForm(false);
                }}
                className="w-full sm:w-auto bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Coupons List */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md overflow-hidden">
        <h2 className="text-xl font-semibold mb-4">Existing Coupons</h2>
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden border border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Min Amount</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {coupons.map((coupon) => (
                    <tr key={coupon._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{coupon.code}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{coupon.discount}%</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">₦{coupon.minAmount.toLocaleString()}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {format(new Date(coupon.expiryDate), 'yyyy-MM-dd')}
                      </td>
                      {/* Modification buttons */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          coupon.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {coupon.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right space-x-3">
                        <button
                          onClick={() => handleEdit(coupon)}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleActive(coupon._id, coupon.active)}
                          className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded"
                        >
                          {coupon.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDelete(coupon._id)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponManager;